import React, {
  useRef,
  useState,
  useCallback,
  createRef,
  CSSProperties,
} from 'react';
import styled, { css } from 'styled-components';
import { AnimatePresence } from 'framer-motion';

import TabBtns from './TabBtns';
import { usePrevious } from '@hooks/usePrevious';
import { TabContents, TabWrapper } from '../tabStyle';
import { ItabType } from '@typings/setting/tabType.interface';
import {
  PAGE_HASHTAG_TYPE,
  PAGE_HASHTAG_TYPE_VALUE,
} from '@typings/setting/pageTagType.interface';

const TabBtnSec = styled.div<{
  isFixed?: boolean;
}>`
  width: 100%;
`;

const spring = {
  type: 'spring',
  stiffness: 800,
  damping: 50,
  duration: 0.05,
  originX: 0.5,
};

type Props = {
  tabList: ItabType[];
  swipe?: boolean;
  order?: number;
  setOrder?: any;
  tabFixed?: boolean;
  isEdit?: boolean;
  btnSideSec?: JSX.Element | JSX.Element[];
  position?: 'flex-start' | 'center' | 'space-between';
  tabBtnSecStyle?: CSSProperties;
  tabId?: string;
  customBtnId?: string;
  style?: CSSProperties;
  noAni?: boolean;
  overflowY?: 'auto' | 'unset';
  noUnderlineAni?: boolean;
  handleOffset?: boolean;
  layout?: PAGE_HASHTAG_TYPE_VALUE;
  tabMaxHeight?: number;
  defaultOrder?: number;
};

function CustomTabs({
  tabList,
  swipe = false,
  setOrder,
  order,
  tabFixed = true,
  btnSideSec,
  position,
  tabBtnSecStyle,
  tabId,
  customBtnId,
  style,
  noAni,
  overflowY = 'auto',
  noUnderlineAni = false,
  handleOffset = true,
  layout,
  tabMaxHeight = 80,
  defaultOrder = 0,
}: Props) {
  const [currentOrder, setCurrentOrder] = useState<number>(defaultOrder);
  const previous = usePrevious(currentOrder);
  const dir = currentOrder < (previous || 0);

  // 터치 반응에 필요한
  // const sliderRef = useRef<HTMLDivElement>(null);
  const sliderRef: any = useRef(
    Array.from({ length: tabList.length }, () => createRef<HTMLDivElement>()),
  );

  const [isMouseDown, setIsMouseDown] = useState(false);
  const [touchX, setTouchX] = useState(0);
  const [walk, setWalk] = useState(0);

  //터치에도 반응하는 슬라이드
  const handleSlideDown = useCallback(
    (e) => {
      if (swipe) {
        setIsMouseDown(true);
        e.preventDefault();

        const pageX = e.pageX || e.changedTouches[0].pageX;
        if (sliderRef.current) {
          setTouchX(pageX - sliderRef.current[currentOrder].current.offsetLeft);
        }
      }
    },
    [currentOrder],
  );

  const handleSlideMove = useCallback(
    (e) => {
      if (swipe && isMouseDown) {
        const pageX = e.pageX || e.changedTouches[0].pageX;
        const startX =
          pageX - sliderRef.current[currentOrder].current.offsetLeft;
        const walk = Math.sign(startX - touchX);
        // 디버깅
        // console.log({ touchX, startX, walk });
        setWalk(walk);
      }
    },
    [isMouseDown, currentOrder, touchX],
  );

  const handleSlideMouseLeave = useCallback((e) => {
    if (swipe) {
      setIsMouseDown(false);
    }
  }, []);

  const handleSlideMouseUp = useCallback(
    (e) => {
      if (swipe) {
        setIsMouseDown(false);
        if (walk < 0) {
          // console.log('nextSlide', walk);
          setCurrentOrder((prev) =>
            prev === tabList.length - 1 ? 0 : prev - walk,
          );
        }
        if (walk > 0) {
          // console.log('prevSlide', walk);
          setCurrentOrder((prev) =>
            prev === 0 ? tabList.length - 1 : prev - walk,
          );
        }
      }
    },
    [walk, currentOrder],
  );
  //터치에도 반응하는 슬라이드 end

  return (
    <TabWrapper style={style}>
      <TabBtns
        tabList={tabList}
        currentOrder={order || currentOrder}
        setCurrent={setOrder || setCurrentOrder}
        tabFixed={tabFixed}
        btnSideSec={btnSideSec}
        position={position}
        style={tabBtnSecStyle}
        tabId={tabId}
        customBtnId={customBtnId}
        noUnderlineAni={noUnderlineAni}
        handleOffset={handleOffset}
        layout={layout}
        tabMaxHeight={tabMaxHeight}
      />

      <>
        <AnimatePresence custom={currentOrder} exitBeforeEnter initial={false}>
          <TabContents
            role="tabpanel"
            overflowY={overflowY}
            ref={sliderRef.current[currentOrder]}
            onMouseDown={handleSlideDown}
            onMouseMove={handleSlideMove}
            onMouseUp={handleSlideMouseUp}
            onMouseLeave={handleSlideMouseLeave}
            // 모바일
            onTouchStart={handleSlideDown}
            onTouchMove={handleSlideMove}
            onTouchEnd={handleSlideMouseUp}
            // 모바일 end
            whileDrag={{ opacity: 0.5 }}
            drag={swipe ? 'x' : false}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            key={currentOrder}
            custom={currentOrder}
            initial="enter"
            animate="in"
            exit="exit"
            transition={!noAni ? spring : {}}
            variants={
              !noAni
                ? {
                  enter: { x: dir ? -5 : 5, top: 0, opacity: 0 },
                  in: { x: 0, opacity: 1, top: 0 },
                  exit: (_current) => ({
                    x: _current > currentOrder ? -5 : 5,
                    opacity: 0,
                  }),
                }
                : {}
            }
          >
            <>{tabList[currentOrder].contents}</>
          </TabContents>
        </AnimatePresence>
      </>
    </TabWrapper>
  );
}

export default CustomTabs;
